import { FC, ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { slugs } from '@/constants/slugs'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import {
  GButtonMD,
  GResponsiveText,
  GStepValueLG,
  GStepValueXL,
  GTitleSM,
  GTitleXL,
  GTitleXS,
  GTitleXXL,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GiveawayHowToStep } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

interface Props {
  steps: GiveawayHowToStep[]
  className?: string
}

export const LevelUp: FC<Props> = ({ steps, className }) => {
  const { t } = useTranslate('tickets')
  const { scrollToProducts, theatricalSlug } = useGiveawayContext()

  return (
    <FlexColumn
      className={classNames('mx-auto max-w-[1080px] gap-4 pb-12 pt-[72px] md:pb-16 md:pt-[120px]', className)}
    >
      <Translate t={t} i18nKey="levelUp">
        <GResponsiveText
          color="giveaway-text-primary-dark"
          MobileTextComponent={GTitleXL}
          DesktopTextComponent={GTitleXXL}
        >
          <span className="text-giveaway-secondary-accent">Level Up</span> in 3 Steps:
        </GResponsiveText>
      </Translate>
      {steps.map((step, index) => (
        <StepValue key={step.title} step={(index + 1).toString().padStart(2, '0')}>
          <span className={'!font-semibold'}>{step.title}</span>: {step.description}
        </StepValue>
      ))}
      <Button
        variant="core-gray-400"
        className={classNames(
          "mt-6 hidden !border-2 !border-giveaway-text-subtle-light px-[24px] pb-[15px] pt-[14px] !font-['bio-sans'] !text-giveaway-text-subtle-light md:block md:w-fit",
          theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-xl',
        )}
        outline
        onClick={scrollToProducts}
      >
        <GButtonMD>{t('preOrderTickets', 'Pre-Order Tickets')}</GButtonMD>
      </Button>
    </FlexColumn>
  )
}

const StepValue = ({ step, children }: { step: string; children: ReactNode }) => (
  <div className="mb-4 flex items-start justify-start gap-2">
    <div className="flex rounded-none bg-giveaway-secondary px-2 py-0.5 ">
      <GResponsiveText
        color="giveaway-text-primary-light"
        className="!font-normal"
        MobileTextComponent={GStepValueLG}
        DesktopTextComponent={GStepValueXL}
      >
        {step}
      </GResponsiveText>
    </div>
    <GResponsiveText
      color="giveaway-text-primary-dark"
      className="!font-normal"
      MobileTextComponent={GTitleXS}
      DesktopTextComponent={GTitleSM}
    >
      {children}
    </GResponsiveText>
  </div>
)
